/**
 * Public API of the behavior registry: typed accessors over the string-keyed kernels, plus the registry
 * mechanics re-exported for admin pickers/validation. Importing this module ensures the built-in
 * behaviors are registered (via the `./core` side effect), so a primitive need only import from here.
 *
 * Each accessor names its behavior key in one place and casts the stored kernel to its real signature,
 * giving call sites full type safety while keeping the indirection seam. Only the jest-safe kernels live
 * here; the heavy `build-report` accessor is co-located in `./report` (imported by the lazy report-editor
 * primitive) so this module stays free of `@react-pdf`. Types are imported type-only, so no heavy value
 * enters this module's graph.
 */
import './core';
import { getBehaviorOrThrow } from './registry';
import type { InstallBaseUpload, WorkshopState } from '../../api/types';
import type { DeepDiveAnalysis } from '../../modules/guide/deepDiveAnalysis';
import type { SlideDraft } from '../../modules/decks/generate';
import type { SourceDeck } from '../../modules/decks/sourceDeck';
import type { LifecycleParse } from '../../modules/installBase/lifecycle';
import type { IqReconcileInput } from './derivations';

export {
  registerBehavior,
  getBehavior,
  getBehaviorOrThrow,
  registeredBehaviorKeys,
  type Behavior,
} from './registry';
export type { IqReconcileInput } from './derivations';

/** Score the deep-dive answers into the ranked findings/plays the gap analysis and report read. */
export function deepDiveScoring(state: WorkshopState): DeepDiveAnalysis {
  return getBehaviorOrThrow<(state: WorkshopState) => DeepDiveAnalysis>('deep-dive-scoring')(state);
}

/** Turn a source deck + kicker into the on-screen slide drafts the session presentation renders. */
export function sourceDeckToDraftsBehavior(deck: SourceDeck, kicker: string): SlideDraft[] {
  return getBehaviorOrThrow<(deck: SourceDeck, kicker: string) => SlideDraft[]>(
    'source-deck-to-drafts',
  )(deck, kicker);
}

/** The Cisco Ready last-date-of-support sentence for the Lifecycle question, or null when nothing applies. */
export function deriveLdosFacts(installBase: InstallBaseUpload | null | undefined): string | null {
  return getBehaviorOrThrow<(installBase: InstallBaseUpload | null | undefined) => string | null>(
    'derive-ldos-facts',
  )(installBase);
}

/** Decide how to reconcile the recorded IQ scenario with what Profiling left open ({ write } or null). */
export function reconcileIqScenario(input: IqReconcileInput): { write: string } | null {
  return getBehaviorOrThrow<(input: IqReconcileInput) => { write: string } | null>(
    'reconcile-iq-scenario',
  )(input);
}

/** Parse an uploaded Cisco Ready install-base export into lifecycle facts (runs in a web worker). */
export function parseInstallBase(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<LifecycleParse> {
  return getBehaviorOrThrow<
    (file: File, onProgress?: (fraction: number) => void) => Promise<LifecycleParse>
  >('install-base-parse')(file, onProgress);
}
